import cx from 'classnames';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../../state/store';
import { applyThousandsSeparator } from '../../../../utils/strings';
import { LrvText } from '../../../../common/components/LrvText/LrvText';
import { getGlobalSmallAnimalsPercentage, weightedAverage } from '../../../Pdfs/TransferGraph/helpers';
import { roundThreeDecimals, roundTwoDecimals, THEME, transferStatuses, weightUnits } from '../../../../config/commons';

import styles from './TransferSummary.module.scss';

interface Props {
  theme?: 'light' | 'dark';
}

export const TransferSummary = (props: Props) => {
  const { theme = 'dark' } = props;
  
  const [t] = useTranslation();
  const isLightTheme = theme === THEME.LIGHT;

  const { parameters, capsules } = useSelector((state: RootState) => state.weighingTransfers);
  const { transfer } = capsules;

  const [averageWeight, setAverageWeight] = useState(0);
  const [uniformityWeight, setUniformityWeight] = useState(0);
  const [variationCoefficientWeight, setVariationCoefficientWeight] = useState(0);

  const [totalTransferredWeight, setTotalTransferredWeight] = useState(0);
  const [animalsTransferred, setAnimalsTransferred] = useState(0);
  const [smallAnimalsPercentage, setSmallAnimalsPercentage] = useState(0);

  useEffect(() => {
    const animalsTransferred = parameters.filter((parameter) => parameter.key === 'animalsInCapsule' || parameter.key === 'animalsInPump').map((parameter) => Number(parameter.value));
    const averageWeights = parameters.filter((parameter) => parameter.key === 'averageWeight').map((parameter) => Number(parameter.value));
    const transferredWeight = parameters.filter((parameter) => parameter.key === 'transferredInCapsule' || parameter.key === 'transferredInPump').map((parameter) => Number(parameter.value));
    const uniformityWeights = parameters.filter((parameter) => parameter.key === 'uniformityWeight').map((parameter) => Number(parameter.value));
    const variationCoefficientWeights = parameters.filter((parameter) => parameter.key === 'variationCoefficientWeight').map((parameter) => Number(parameter.value));
    const animalsSmallerThan = parameters.filter((parameter) => parameter.key === 'animalsSmallerThan').map((parameter) => Number(parameter.value));

    const averageWeight = weightedAverage({ values: averageWeights, weights: transferredWeight });
    const uniformityWeight = weightedAverage({ values: uniformityWeights, weights: transferredWeight });
    const variationCoefficientWeight = weightedAverage({ values: variationCoefficientWeights, weights: transferredWeight });
    
    const totalTransferredWeight = transferredWeight.reduce((sum, weight) => sum + weight, 0);
    const totalAnimalsTransferred = animalsTransferred.reduce((sum, weight) => sum + weight, 0);
    const smallAnimalsPercentage = getGlobalSmallAnimalsPercentage({ animalsTransferred, animalsSmallerThan, totalAnimalsTransferred });

    setAverageWeight(averageWeight);
    setUniformityWeight(uniformityWeight);
    setVariationCoefficientWeight(variationCoefficientWeight);
    
    setTotalTransferredWeight(totalTransferredWeight);
    setAnimalsTransferred(totalAnimalsTransferred);
    setSmallAnimalsPercentage(smallAnimalsPercentage);
  }, [parameters]);

  /*render components section */
  const renderTransferData = (props: {value: number | string; label: string; unit?: string}) => {
    const { label, value, unit = '' } = props;

    return (
      <div>
        <div className={styles.containerValue}>
          <LrvText theme={theme} text={`${value} ${unit}`}/>
        </div>

        <div className={styles.containerLabel}>
          <LrvText theme={theme} text={label} className={cx(styles.label, isLightTheme ? styles.lightLabel : styles.darkLabel)}/>
        </div>
      </div>
    );
  };
  /* End of render components section */

  if (transfer.status === transferStatuses.ACTIVE) {
    return null;
  }

  return (
    <div className={cx(styles.container, isLightTheme ? styles.lightContainer : styles.darkContainer)}>
      {renderTransferData({ value: applyThousandsSeparator(roundTwoDecimals(totalTransferredWeight)), label: t('transfers.animals'), unit: weightUnits.KG })}
      {renderTransferData({ value: applyThousandsSeparator(animalsTransferred), label: t('transfers.animalsTransferred') })}
      {renderTransferData({ value: roundTwoDecimals(smallAnimalsPercentage), label:  `${t('transfers.parameters.animalsSmallerThan', { value: transfer?.maxSmallAnimalsWeight })} ${weightUnits.G}`, unit: '%' })}
      
      {renderTransferData({ value: roundThreeDecimals(averageWeight), label: t('transfers.parameters.averageWeight'), unit: weightUnits.G })}
      {renderTransferData({ value: roundTwoDecimals(uniformityWeight), label: t('transfers.parameters.uniformityWeight'), unit: '%' })}
      {renderTransferData({ value: roundTwoDecimals(variationCoefficientWeight), label: t('transfers.parameters.variationCoefficientWeight'), unit: '%' })}
    </div>
  );
};
